import React, { useEffect } from 'react';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, Box,
} from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { Link as ReactLink, useLocation } from 'react-router-dom';
import { Crumb, useBreadcrumbs } from './BreadcrumbsProvider';

export type { Crumb } from './BreadcrumbsProvider';

// item 417 — a quiet clickable trail so deep edits have an easy way back
// (Projects → project → document). Deliberately plain (no glass) so it
// reads the same in the Aurora and Accountant skins.
const truncate = (s: string) => (s.length > 40 ? `${s.slice(0, 39)}…` : s);

// The visual trail — rendered by the sticky header (item 456) and layer panels
// (item 455), not by pages directly.
export const BreadcrumbTrail: React.FC<{ crumbs: Crumb[] }> = ({ crumbs }) => {
  if (crumbs.length === 0) return null;
  return (
    <Breadcrumb
      spacing="6px"
      separator={<Box as={FiChevronRight} fontSize="12px" opacity={0.6} />}
      fontSize="sm"
      opacity={0.75}
      sx={{ ol: { flexWrap: 'wrap' } }}
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <BreadcrumbItem key={`${c.label}-${c.to ?? 'here'}`} isCurrentPage={isLast}>
            {isLast || !c.to ? (
              <Box as="span" fontWeight="medium">{truncate(c.label)}</Box>
            ) : (
              <BreadcrumbLink as={ReactLink} to={c.to} _hover={{ textDecoration: 'underline', opacity: 1 }}>
                {truncate(c.label)}
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};

// item 456 — the header-side renderer: shows the trail registered for a given
// route path (the canvas path in the sticky header; the panel path inside a panel).
export const HeaderBreadcrumbs: React.FC<{ path: string }> = ({ path }) => {
  const { trails } = useBreadcrumbs();
  const crumbs = trails[path];
  if (!crumbs || crumbs.length === 0) return null;
  return (
    <Box px={{ base: 4, md: 8 }} pt={1}>
      <BreadcrumbTrail crumbs={crumbs} />
    </Box>
  );
};

// item 456 — pages keep calling <FormBreadcrumbs crumbs={...}/> exactly as before
// (item 417), but it now registers the trail with the shell instead of rendering
// inline, so the trail lives in the sticky header and never scrolls away.
const FormBreadcrumbs: React.FC<{ crumbs: Crumb[] }> = ({ crumbs }) => {
  const { pathname } = useLocation();
  const { register, unregister } = useBreadcrumbs();
  // Serialize so a fresh-but-identical array from a parent re-render doesn't loop
  const serialized = JSON.stringify(crumbs);
  useEffect(() => {
    register(pathname, JSON.parse(serialized));
    return () => unregister(pathname);
  }, [pathname, serialized, register, unregister]);
  return null;
};

export default FormBreadcrumbs;
